
import { useEffect, useRef } from "react";
import { useGameState } from "./GameState";
import { useGameInitialization } from "./hooks/useGameInitialization";
import { useGuessSubmission } from "./hooks/useGuessSubmission";
import { GameStateManagerResult } from "./types/GameTypes";
import { toast } from "sonner";
import { updateKeyboardStatusFromGuesses } from "./managers/KeyboardStateManager";

export const useGameStateManager = (
  currentWord: string,
  onCorrectGuess: () => void,
  onGameOver: () => void,
  onAttempt: () => void,
): GameStateManagerResult => {
  const gameState = useGameState();
  const {
    guesses,
    setGuesses,
    currentGuess,
    setCurrentGuess,
    keyboardStatus,
    setKeyboardStatus,
    setIsValidating,
    previousWord,
    setPreviousWord,
    setIsTransitioning,
    levelAttempts,
    setLevelAttempts,
    showGameCompletion,
    setShowGameCompletion,
    gameStatus,
    setGameStatus,
    guessHistory,
    setGuessHistory,
    setInvalidGuessType,
    isLoading,
  } = gameState;

  // Game initialization logic
  const {
    isInitialized,
    initError,
    isLoading: isInitLoading,
    initializeGameState
  } = useGameInitialization(currentWord);
  
  // Prevent unnecessary initialization attempts
  const initAttempted = useRef(false);
  // Track if keyboard status needs update
  const keyboardUpdatedRef = useRef(false);
  // Track mode switches
  const modeSwitchHandledRef = useRef(false);
  // Track current mode to detect changes
  const currentModeRef = useRef(localStorage.getItem('babblegram_game_mode') || 'daily');

  // Guess submission logic
  const {
    isValidating,
    invalidGuessType,
    isTransitioning,
    submitGuess
  } = useGuessSubmission(currentWord, onCorrectGuess, onGameOver, onAttempt);

  // Initialize game state when component mounts, but only once per word
  useEffect(() => {
    if (!currentWord || initAttempted.current) return;
    
    // Check if mode has changed
    const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
    const modeChanged = currentModeRef.current !== currentMode;
    
    if (modeChanged) {
      console.log('GameStateManager - Mode changed detected', {
        from: currentModeRef.current,
        to: currentMode,
        timestamp: new Date().toISOString()
      });
      currentModeRef.current = currentMode;
      // Reset initialization flag when mode changes
      initAttempted.current = false;
    }
    
    console.log('GameStateManager - Initializing game state for word:', {
      word: currentWord,
      attempted: initAttempted.current,
      modeChanged,
      guessCount: guesses.length,
      timestamp: new Date().toISOString()
    });
    
    initAttempted.current = true;
    
    // Critical fix: Apply guesses first before initializing other state
    initializeGameState(
      setGuesses,
      setKeyboardStatus,
      setPreviousWord,
      setLevelAttempts,
      setGuessHistory,
      setGameStatus
    );
    
    // Reset the flag when word changes
    return () => {
      initAttempted.current = false;
      keyboardUpdatedRef.current = false;
    };
  }, [currentWord, initializeGameState]);

  // Function to reset the game state (used after game reset)
  const resetGameState = () => {
    console.log('Resetting game state in GameStateManager');
    
    // Reset all state
    setGuesses([]);
    setCurrentGuess('');
    setKeyboardStatus({});
    setPreviousWord(null);
    setLevelAttempts([]);
    setGameStatus('playing');
    setGuessHistory([]);
    setInvalidGuessType(null);
    setIsValidating(false);
    setIsTransitioning(false);
    setShowGameCompletion(false);
    
    // Reset initialization flags
    initAttempted.current = false;
    keyboardUpdatedRef.current = false;
    modeSwitchHandledRef.current = false;
  };

  // Listen for mode switch events to reinitialize state
  useEffect(() => {
    const handleModeSwitch = (event: Event) => {
      const customEvent = event as CustomEvent;
      // Reset initialization flag to force reinitialization
      initAttempted.current = false;
      
      console.log('Mode switch event detected in GameStateManager, reinitializing state', {
        detail: customEvent.detail,
        timestamp: new Date().toISOString()
      });
      
      // Update current mode ref
      const newMode = localStorage.getItem('babblegram_game_mode') || 'daily';
      currentModeRef.current = newMode;
      
      // Delay slightly to ensure mode context is updated
      setTimeout(() => {
        // Force re-initialization with current word
        initializeGameState(
          setGuesses,
          setKeyboardStatus,
          setPreviousWord,
          setLevelAttempts,
          setGuessHistory,
          setGameStatus
        );
      }, 0);
    };
    
    window.addEventListener('babblegram_mode_switched', handleModeSwitch);
    
    return () => {
      window.removeEventListener('babblegram_mode_switched', handleModeSwitch);
    };
  }, [initializeGameState]);

  // Update keyboard status whenever guesses change, but only once per guess update
  useEffect(() => {
    if (guesses.length > 0 && !keyboardUpdatedRef.current) {
      console.log('GameStateManager - Guesses have been updated:', {
        guessCount: guesses.length,
        guessDetails: guesses.map(g => g.map(l => l.letter).join('')),
        timestamp: new Date().toISOString()
      });
      
      // Set flag to prevent repeated updates in the same render cycle
      keyboardUpdatedRef.current = true;
      
      // Update keyboard status based on current guesses
      const updatedKeyboardStatus = updateKeyboardStatusFromGuesses(guesses);
      
      // Only update if there are actual changes to prevent loops
      const hasChanges = !Object.keys(updatedKeyboardStatus).every(key => 
        keyboardStatus[key] === updatedKeyboardStatus[key]
      );
      
      if (hasChanges || Object.keys(keyboardStatus).length !== Object.keys(updatedKeyboardStatus).length) {
        setKeyboardStatus(updatedKeyboardStatus);
      }
      
      // Reset the flag on the next tick to allow future updates
      setTimeout(() => {
        keyboardUpdatedRef.current = false;
      }, 0);
    }
  }, [guesses]);

  // If there's an initialization error, we want to prevent further game actions
  if (initError) {
    console.error('Game state initialization error:', initError);
    return {
      ...gameState,
      isValidating,
      invalidGuessType,
      isTransitioning,
      resetGameState,
      handleSubmitGuess: async () => {
        toast.error("Game state not properly initialized. Please refresh the page.");
        return Promise.resolve();
      }
    };
  }

  // Main game submit function that will be exposed to consumers
  const handleSubmitGuess = async () => {
    await submitGuess(
      currentGuess,
      guesses,
      guessHistory,
      levelAttempts,
      gameStatus,
      keyboardStatus,
      setGuesses,
      setCurrentGuess,
      setKeyboardStatus,
      setGameStatus,
      setShowGameCompletion,
      setGuessHistory,
      setLevelAttempts,
      setPreviousWord
    );
  };

  return {
    ...gameState,
    isValidating,
    invalidGuessType,
    isTransitioning,
    resetGameState,
    handleSubmitGuess
  };
};
